import { HttpClient } from '@angular/common/http';
import { Component, ElementRef, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { CellEditingStoppedEvent, ICellRendererParams, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { ContactTypeEditorComponent } from 'src/app/contacts/contact-type-editor/contact-type-editor.component';
import { CustomRendererComponent, HeaderRendererComponent } from '../custom-renderer/custom-renderer.component';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ShipmentListsModalComponent } from '../../shipment-lists/shipment-lists-modal/shipment-lists-modal.component';
import { NgZone } from '@angular/core';

@Component({
  selector: 'app-trip-details',
  templateUrl: './trip-details.component.html',
  styleUrls: ['./trip-details.component.scss']
})
export class TripDetailsComponent implements ICellRendererAngularComp, OnInit {

  detailsForm: FormGroup = this.fb.group({
    date: '',
    created: '',
    asn_number: '',
    trip_number: '',
    external_bill_of_lading: '',
    store_number: '',
    total_cartons: '',
    manifest_carton_count: '',
    bol_carton_count: '',
    reported_weight: '',
    status: '',
    week_no: '',
    alternate_reference: '',
    manifest_id: '',
  });
  trip_details: any;
  trip_list_details: any;
  trip_list_uuid!: string;
  pallets: Array<{ [key: string]: string | number | object | boolean }> = [];
  barcodes: Array<{ [key: string]: string | number | object | boolean }> = [];
  shipments: Array<{ [key: string]: string | number | object | boolean }> = [];

  typeListBarcodeStatus = [
    '',
    'MANUAL AUDIT',
    'MISSING',
    'OVERAGE',
    'OPENED',
    'BROKEN',
    'CRUSHED',
    'WET',
    'HOLDOVER'
  ]

  typeListCartonResolution = [
    '',
    'DELIVERED AS IS',
    'HELD FOR DISPOSITION',
    'RETAPED AND DELIVERED',
    'REPACKAGED AND DELIVERED',
    'DISCARDED DISPOSED'
  ]

  shipmentColumnDefs = [
    { field: 'shipment_list_uuid', headerName: 'UUID', hide: true },
    { field: 'alternate_reference', 
      width: 120,
      headerName: 'FBI No.', 
      cellRenderer: (params: any) => {
      return `<a style="text-decoration: none;" href="/full-order-details/${params.data.lading_uuid}">${params.value}</a>`;
      } 
    },    
    { field: 'asn_number', headerName: 'ASN No.', cellRenderer: 'agGroupCellRenderer' },
    { field: 'trip_number', headerName: 'Trip No.', cellRenderer: 'agGroupCellRenderer' },    
    {
      field: 'status', headerName: 'Status', cellRenderer: this.statusTypeFormatting, filter: 'agSetColumnFilter', filterParams: {
        values: [
          'Receiving in Progress',
          'Received Completed',
          'Auditing in Progress',
          'Audit Completed',
          'Work Order Created',
          'Container Created',
          'Intermodal Created',
        ]
      },
    },
    { field: 'created', headerName: 'Date of Scan' },
    { field: 'total_cartons', headerName: 'Expected', width: 140 },
    { field: 'act_trail_number', headerName: 'ACT Trailer No.', width: 170 },
    { field: 'external_bill_of_lading', headerName: 'Bill Of Lading', width: 170 },    
    { field: 'store_number', headerName: 'Store No.', width: 120 },
    { field: 'week_no', headerName: 'Week No.', width: 130 },
    {
      headerName: 'Action', cellRenderer: (params: any) => {
        const button = document.createElement('button');
        const icon = document.createElement('i');

        // Add Font Awesome classes to the icon element
        icon.classList.add('fas', 'fa-edit'); 

        // Append the icon to the button
        button.appendChild(icon);
        button.addEventListener('click', () => {
          this.zone.run(() => {
            this.editShipment(params);
          });
        });
        return button;
      }, editable: false, width: 100,
    },       
  ]
  
  onButtonClick = () => {
    // Handle the button click logic here
    console.log('Button clicked');
  }

  onGridReady(params: any) {
    // Access the grid API and adjust column definitions after grid is ready
    // const newColumnDefs = this.shipmentColumnDefs
    // params.api.setColumnDefs(newColumnDefs);
  }

  palletsColumnDefs = [
    { field: 'load_no', headerName: 'Load No.' },
    { field: 'store_number', headerName: 'Store No.', width: 120 },    
    { field: 'length', headerName: 'Length' },
    { field: 'width', headerName: 'Width' },
    { field: 'height', headerName: 'Height' },
    { field: 'weight', headerName: 'Weight' },
    { field: 'cartons_pallet', headerName: 'Carton Count' },
  ]

  // headers for the barcodes grid
  barcodeColumnDefs = [
    {
      headerName: 'Count',
      valueGetter: this.countGetter
    },
    { field: 'store_number', headerName: 'Store No.', width: 120 },    
    { field: 'barcode', headerName: 'Barcode' },
    {
      field: 'received_in',
      headerName: 'Received In',
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === "false" ? '❌' : '✅';
      }
    },
    {
      field: 'audited',
      headerName: 'Audited',
      valueFormatter: function (params: ValueFormatterParams) {
        return params.value === "false" ? '❌' : '✅';
      }
    },
    {
      field: 'return_status',
      headerName: 'Status',
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: {
        multiple: false,
        types: this.typeListBarcodeStatus
      }
    },
    {
      field: 'carton_resolution',
      headerName: 'Carton Resolution',
      cellEditor: ContactTypeEditorComponent,
      cellEditorPopup: true,
      cellEditorParams: {
        multiple: false,
        types: this.typeListCartonResolution
      }
    },
  ]

  defaultColDef = {
    sortable: true,
    filter: 'agTextColumnFilter',
    filterParams: {
      caseSensitive: false,
    },
    autoHeight: true,
    cellClass: 'cell-wrap-text',
    resizable: true,
    editable: true
  };

  constructor(private http: HttpClient, 
              private fb: FormBuilder, 
              private router: Router, 
              private elementRef: ElementRef, 
              private dialog: MatDialog, 
              private zone: NgZone) { }

  ngOnInit(): void {
  }

  agInit(params: any): void {
    if (params.data) {
      const url = "/api/shipment_lists/trip_lists.php"
      this.http.get(url, { params: { name: 'shipment_and_receiving', trip_list_uuid: params.data.trip_list_uuid, command: 'TRIP_DETAILS' } }).subscribe((response: any) => {
        if (response) {
          console.log('hello world: ', response);
          this.barcodes = response.data.barcode_list;
          this.pallets = response.data.pallet_list;
          this.shipments = response.data.shipment_list;
          this.trip_list_details = response.data.detail;
          this.trip_list_uuid = response.data.detail.trip_list_uuid;
          this.trip_details = params.data;
          this.detailsForm = this.fb.group({
            date: [this.trip_details.date],
            created: [this.trip_details.created],
            asn_number: [this.trip_details.asn_number],
            trip_number: [this.trip_details.trip_number],
            external_bill_of_lading: [this.trip_details.external_bill_of_lading],
            store_number: [this.trip_list_details.store_number],
            total_cartons: [this.trip_details.total_cartons],
            manifest_carton_count: [this.trip_details.manifest_carton_count],
            bol_carton_count: [this.trip_details.bol_carton_count],
            reported_weight: [this.trip_details.reported_weight],
            status: [this.trip_details.status],
            week_no: [this.trip_details.week_no],
            // appointment_day: [this.operation.appointment_day],
            // appointment_start: [this.operation.appointment_start],
            // appointment_end: [this.operation.appointment_end],
            // contact_organization: [this.carrier.contact_organization],
            // express_road: [this.consignee_accounting.express_road],
            // ab_carbon_fuel_tax: [this.consignee_accounting.ab_carbon_fuel_tax],
            // bc_carbon_fuel_tax: [this.consignee_accounting.bc_carbon_fuel_tax]
          })
          this.detailsForm.disable();
        }
      })

    }
  }

  private countGetter(params: ValueGetterParams) {
    if (params.node && params.node.rowIndex !== null) {
      return params.node.rowIndex + 1
    }
    return ''
  }

  // function to retrieve the appropriate Pallet Number of a barcode (carton) by
  // comparing the package_uuids by looping through the array of pallets and retrieving the index+1
  private getPalletNumber(params: ValueFormatterParams): string {
    if (params.data && params.node && params.node.rowIndex !== null) {
      const packageUuid = params.data.package_uuid;
      const index = this.pallets.findIndex(pallet => pallet['package_uuid'] === packageUuid);
      return index !== -1 ? String(index + 1) : '';
    }
    return '';
  }

  onCellEditingStopped(event: CellEditingStoppedEvent) {

    const editedValue = event.value;
    const column = event.column;
    const rowNode = event.node;

    const product_uuid = rowNode.data.product_uuid
    const return_status = rowNode.data.return_status
    const carton_resolution = rowNode.data.carton_resolution

    const barcode_status_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=UPDATE_BARCODE_STATUS`;
    const carton_resolution_url = `/api/shipment_lists/shipment_details.php?table=shipment_and_receiving&command=PRODUCT_STATUS_UPDATE`;

    const columnName = column.getColId();

    console.log('Edited Value:', editedValue);
    console.log('Row Data:', rowNode.data);
    console.log('Column Name:', columnName)

    // create POST body of cell edited of column, columnName, and value changed, editedValue
    var postData = {
      [columnName]: editedValue,
      product_uuid: product_uuid
    }

    // if an edit is made in the status section of the barcodes subgrid
    if (columnName === 'return_status') {

      postData = {
        [columnName]: editedValue,
        product_uuid: product_uuid,
      }

      console.log('Post Data:', postData);
      console.log('Stringify:', JSON.stringify(postData))

      return this.http.post<any>(barcode_status_url, postData)
        .subscribe(response => {
          console.log('Post Response: ', response)
        })

      // if an edit is made in the carton resolution section of the barcodes grid
    } else if (columnName === 'carton_resolution') {

      postData = {
        [columnName]: editedValue,
        product_uuid: product_uuid,
        return_status: return_status
      }

      console.log('Post Data:', postData);
      console.log('Stringify:', JSON.stringify(postData))

      return this.http.post<any>(carton_resolution_url, postData)
        .subscribe(response => {
          console.log('Post Response: ', response)
        })
    } else {
      return
    }
  }

  statusTypeFormatting(params: ICellRendererParams) {
    let statusTypes: string[] = []
    if (params.value != null) {
      statusTypes = params.value.split(",");
    }
    let buttonsHtml = '';

    for (let type of statusTypes) {
      let trimmedType = type.trim();
      let bgColor = '';

      switch (trimmedType) {
        case 'Work Order Created':
          bgColor = '#FF0000';
          break;
        case 'Container Created':
            bgColor = '#FA7878';
            break;
        case 'Intermodal Created':
              bgColor = '#F9A5A5';
              break;                  
        case 'Audit Completed':
          bgColor = '#00FF00';
          break;
        case 'Receiving in Progress':
          bgColor = '#99CCFF';
          break;
        case 'Received Completed':
          bgColor = '#3399FF';
          break;
        case 'Auditing in Progress':
          bgColor = "#CCFF99"
          break;
        default:
          bgColor = '#FFFFFF';
      }

      buttonsHtml += '<button class="btn dropdown-toggle button-text-wrapper" style="background-color:' + bgColor + ';border-radius:10px;font-weight:bold;">' + trimmedType + '</button> <br>';
    }

    let element = '<div class="btn-group">' + buttonsHtml + '</div>';

    return element;
  }

  editTripDetails() {
    const dialogConfig = new MatDialogConfig();       

    dialogConfig.width = '350px'; // You can use pixels or other units
    dialogConfig.height = '650px';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Trip Details',
        modalMessage: {trip_list_uuid: this.trip_list_uuid, 
                       week_no: this.trip_details.week_no, 
                       external_bill_of_lading: this.trip_details.external_bill_of_lading,
                       manifest_carton_count: this.trip_details.manifest_carton_count,
                       bol_carton_count: this.trip_details.bol_carton_count,
                       trip_number: this.trip_details.trip_number,
                       asn_number: this.trip_details.asn_number
        },
      },
    };
    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }
  
  editShipment(params: any) {
    const dialogConfig = new MatDialogConfig();
    const shipment_list_uuid = params.data.shipment_list_uuid;
    const act_trail_number = params.data.act_trail_number;    
    const external_bill_of_lading = params.data.external_bill_of_lading;

    console.log('Edit Trip Shipment:', params.data);      

    dialogConfig.width = '10'; // You can use pixels or other units
    dialogConfig.height = '10';
    dialogConfig.data = {
      data: {
        modalTitle: 'Edit Trip Shipment',
        modalMessage: {shipment_list_uuid: shipment_list_uuid, 
                       act_trail_number: act_trail_number, 
                       external_bill_of_lading: external_bill_of_lading
                      },
      },
    };

    console.log('Dialog Config:', dialogConfig);

    this.dialog.open(ShipmentListsModalComponent, dialogConfig);
  }  

  viewFullTrip(): void {
    this.router.navigate(['/full-trip-details', this.trip_list_uuid]);
  }

  viewPrint(): void {
    this.router.navigate(['/trip_print', this.trip_list_uuid]);
  }

  createTrip() {
    this.router.navigate(['/trip-creator']);
  }

  refresh(params: any): boolean {
    return false;
  }
}
